import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X } from 'lucide-react';

import { floatingSurfaceClass } from '../floatingSurfaceStyles';
import { buildProgressBars } from '../../../utils/shipChanges';
import { formatEtaDate, timelinePct } from '../../../utils/shipmentSchedule';
import { shipmentStatusKey } from '../../../utils/shipmentChipFilters';
import { STATUS_COLORS } from './modeIcons';
import { rowTransportMode } from '../../../utils/modeRows';
import VesselBoardFlow from '../tracking/VesselBoardFlow';
import { useTheme } from '../../../context/ThemeContext';

const SURFACE = floatingSurfaceClass(97);

const LABEL = "font-['Inter'] text-[11px] font-extrabold tracking-[0.42px] text-[#9CA3AF] uppercase dark:text-[#6B716C]";
const VALUE = "font-['Inter'] text-[12px] font-semibold text-[#343440] dark:text-[#E6E8E6]";
const SECTION = "font-['Inter'] text-[11px] font-extrabold tracking-[0.42px] text-[#5D625F] uppercase dark:text-[#9AA09A]";

const MODE_TAG = { air: 'AIR', truck: 'TRUCK', sea: 'VESSEL' };

/**
 * Air and truck rows have one leg and no voyage stops, so `buildProgressBars`
 * (which reads ShipsGo stops / tracked groups / legs) returns nothing for them.
 * Their schedule still describes exactly one departure -> arrival hop, which is
 * the same shape a Progress block draws — so synthesise it rather than leaving
 * the section blank.
 */
const modeProgressStops = (shipment) => {
  const port = [shipment?.arrival_port, shipment?.destination_country].filter(Boolean).join(', ');
  if (!port && !shipment?.eta) return [];
  return [{
    key: `${rowTransportMode(shipment)}-leg`,
    port,
    leftRaw: shipment?.etd || null,
    rightRaw: shipment?.eta || null,
    delay_days: shipment?.delay_days ?? null,
    transit_percentage: shipment?.transit_percentage,
  }];
};

/** "1,064,119.4 kg" — the design's format; a trailing .0 is dropped. */
const fmtWeight = (kg) => {
  const n = Number(kg);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;
};

const firstValue = (...vals) => {
  for (const v of vals) {
    const s = typeof v === 'string' ? v.trim() : v;
    if (s !== null && s !== undefined && s !== '') return s;
  }
  return null;
};

/** Key–value line in the Voyage block. Rendered only when there is a value. */
const VoyageRow = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex min-h-[24px] items-center gap-[8px] py-[2px]">
      <span className={`shrink-0 ${LABEL}`}>{label}</span>
      <span className={`ml-auto min-w-0 truncate text-right ${VALUE}`}>{value}</span>
    </div>
  );
};

/**
 * One Progress block per stop: "Stop N", the port, a bar with the percentage
 * inside it, then ETD on the left and ETA · delay on the right.
 */
const ProgressStop = ({ index, row, statusColor, shipmentEtd }) => {
  const { t } = useTranslation();

  // transit_percentage is the carrier's own figure; timelinePct is ours from
  // the dates. Prefer the carrier's and fall back, so a stop with a feed
  // percentage never disagrees with the feed.
  const feedPct = Number(row?.transit_percentage);
  const datePct = timelinePct(row?.leftRaw || shipmentEtd, row?.rightRaw);
  const pct = Number.isFinite(feedPct)
    ? Math.max(0, Math.min(100, Math.round(feedPct)))
    : (datePct ?? 0);

  const delay = Number(row?.delay_days);
  const hasDelay = Number.isFinite(delay);
  const late = hasDelay && delay > 0;
  const etd = formatEtaDate(row?.leftRaw || shipmentEtd);
  const eta = formatEtaDate(row?.rightRaw);

  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-center gap-[6px]">
        <span className={`shrink-0 ${LABEL}`}>
          {t('map.shipment.stop', 'Stop {{n}}', { n: index + 1 })}
        </span>
        <span className={`ml-auto min-w-0 truncate text-right ${VALUE}`}>{row?.port || '—'}</span>
      </div>

      {/* The percentage sits inside the bar, so the track is tall enough to
          hold 10px type rather than the 8px used by the container split.
          White reads on the fill, which is where the label sits for the
          high percentages the design shows — but a short bar leaves the
          centred label on the pale track, so it flips to the dark ink there. */}
      <div className="relative h-[16px] w-full overflow-hidden rounded-[8px] bg-[#F1F3F0] dark:bg-[#252B27]">
        <span
          className="absolute inset-y-0 left-0 rounded-[8px] transition-[width] duration-300"
          style={{ width: `${pct}%`, backgroundColor: statusColor }}
        />
        <span
          className={`absolute inset-0 flex items-center justify-center font-['Inter'] text-[10px] font-extrabold ${
            pct >= 50 ? 'text-white' : 'text-[#1F2937] dark:text-[#E6E8E6]'
          }`}
        >
          {pct}%
        </span>
      </div>

      <div className="flex items-center gap-[8px]">
        <span className={`shrink-0 ${LABEL}`}>
          {etd ? t('map.shipment.etd', 'ETD {{date}}', { date: etd }) : '—'}
        </span>
        <span className="ml-auto flex min-w-0 shrink items-center gap-[4px] truncate">
          <span className={VALUE}>{eta || '—'}</span>
          {hasDelay ? (
            <>
              <span className={LABEL}>·</span>
              {/* 0d is on time and reads green; anything late reads red. */}
              <span
                className={`font-['Inter'] text-[12px] font-extrabold ${
                  late ? 'text-[#DC2626]' : 'text-[#16A34A] dark:text-[#22C55E]'
                }`}
              >
                {t('map.shipment.delayDays', '{{n}}d', { n: delay })}
              </span>
            </>
          ) : null}
        </span>
      </div>
    </div>
  );
};

/**
 * Shipment details popup for Shipments mode.
 *
 * Sizing is the caller's: the panel fills the height its column gives it and
 * scrolls its body, which is how "down to 24px from the bottom of the map"
 * stays true without this component knowing where the map ends.
 *
 * The header does not scroll — it is a flex sibling of the scroll region
 * rather than `sticky`, so a long commodity list cannot ride up over the
 * first progress block.
 */
const ShipmentDetailsPanel = ({ shipment, onClose, onAskBiq, className = '' }) => {
  // The board flow paints an SVG, so it needs the theme as a value — this
  // panel otherwise styles entirely through Tailwind's `dark:` variant.
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const statusKey = shipment ? shipmentStatusKey(shipment) : 'on_time';
  const statusColor = STATUS_COLORS[statusKey] || STATUS_COLORS.on_time;
  const statusLabel = t(`map.filters.shipmentStatus.${statusKey}`, statusKey);

  const mode = rowTransportMode(shipment);
  const isSea = mode === 'sea';
  const stops = useMemo(
    () => (isSea ? buildProgressBars(shipment) || [] : modeProgressStops(shipment)),
    [shipment, isSea],
  );
  // Container split is a sea concept (one vessel, cargo split across PODs).
  // The container-split card was removed in review: it repeated the Voyage
  // vessel and did not show the connection. The board flow below replaces it.
  const showBoardFlow = isSea;

  if (!shipment) return null;

  const origin = firstValue(shipment.origin_country, shipment.origin, shipment.departure_port);
  const containerId = firstValue(shipment.container_number);
  const containers = firstValue(shipment.total_containers);
  const commodities = firstValue(shipment.commodities) || t('map.shipment.noCommodities', 'Shipment');

  // No `uppercase` here: each tag already carries the casing the design uses —
  // "On time" from i18n, the literal "VESSEL"/"AIR"/"TRUCK", "56 containers".
  // Forcing it shouted all three.
  const Tag = ({ children, dotColor }) => (
    <span className="flex h-[22px] shrink-0 items-center gap-[5px] rounded-[6px] bg-[#F1F3F0] px-[7px] font-['Inter'] text-[10px] font-extrabold tracking-[0.3px] whitespace-nowrap text-[#5D625F] dark:bg-[#252B27] dark:text-[#9AA09A]">
      {dotColor ? (
        <span className="h-[6px] w-[6px] shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
      ) : null}
      {children}
    </span>
  );

  return (
    <div
      className={`flex min-h-0 w-full flex-col overflow-hidden rounded-[14px] border border-solid lg:w-[344px] ${SURFACE} ${className}`}
    >
      {/* Fixed header */}
      <div className="shrink-0 border-b border-solid border-[#EEF0EC] px-[12px] pt-[12px] pb-[10px] dark:border-[#2A302C]">
        <div className="flex items-start gap-[8px]">
          <div className="min-w-0 flex-1">
            <p className="font-['Inter'] text-[13px] font-extrabold break-words text-[#343440] dark:text-[#E6E8E6]">
              {commodities}
            </p>
            <p className="mt-[2px] font-['Inter'] text-[11px] font-semibold text-[#828382]">
              {origin ? `${t('map.shipment.origin', 'Origin')}: ${origin}` : t('map.shipment.origin', 'Origin')}
              {containerId ? ` · ${containerId}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="flex h-[24px] w-[24px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#F3F5F2] text-[#5D625F] transition-colors hover:bg-[#E8EDEA] dark:bg-[#252B27] dark:text-[#9AA09A]"
          >
            <X size={13} />
          </button>
        </div>

        <div className="mt-[8px] flex flex-wrap items-center gap-[6px]">
          <Tag dotColor={statusColor}>{statusLabel}</Tag>
          <Tag>{MODE_TAG[mode]}</Tag>
          {/* Air and truck positions are interpolated from the schedule, never
              observed. The old map popup said so and the panel must not quietly
              present them as tracked. */}
          {shipment.estimated ? <Tag>{t('map.shipment.estimated', 'ESTIMATED')}</Tag> : null}
          {containers ? (
            <Tag>{t('map.shipment.containersCount', '{{n}} containers', { n: containers })}</Tag>
          ) : null}
          {/* Labelled outlined pill at the end of the tag row, per the design —
              an unlabelled square gave no hint that it opens BiQ. */}
          {onAskBiq ? (
            <button
              type="button"
              onClick={() => onAskBiq(shipment)}
              title={t('map.shipment.askBiq', 'Ask BiQ about this shipment')}
              className="ml-auto flex h-[22px] shrink-0 cursor-pointer items-center gap-[4px] rounded-[6px] border border-solid border-[#16A34A]/35 bg-[#F0FDF4] px-[7px] font-['Inter'] text-[10px] font-extrabold whitespace-nowrap text-[#16A34A] transition-colors hover:bg-[#DCF0E2] dark:border-[#7EE6A4]/30 dark:bg-[#1C3023] dark:text-[#7EE6A4]"
            >
              <MessageSquare size={11} className="shrink-0" />
              {t('map.shipment.askBiqShort', 'Ask BiQ')}
            </button>
          ) : null}
        </div>
      </div>

      {/* Scrolling body */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[12px] py-[12px]">
        {stops.length > 0 ? (
          <div className="flex flex-col gap-[14px]">
            <p className={SECTION}>{t('map.shipment.progress', 'Progress')}</p>
            {stops.map((row, i) => (
              <ProgressStop
                key={row.key}
                index={i}
                row={row}
                statusColor={statusColor}
                shipmentEtd={shipment.etd}
              />
            ))}
          </div>
        ) : null}

        {/* The vessel board flow, restored here per review: feeders converge
            on the current vessel, which then splits to its discharge ports.
            It renders null when a shipment has neither, so it can be mounted
            unconditionally — the divider goes with it rather than leaving a
            stray rule above nothing. */}
        {showBoardFlow ? (
          <>
            <div className="my-[12px] h-px w-full bg-[#EEF0EC] dark:bg-[#2A302C]" />
            <VesselBoardFlow shipment={shipment} isDark={isDark} />
          </>
        ) : null}

        <div className="my-[12px] h-px w-full bg-[#EEF0EC] dark:bg-[#2A302C]" />
        <p className={`mb-[4px] ${SECTION}`}>{t('map.shipment.voyage', 'Voyage')}</p>
        <div className="flex flex-col">
          {/* The carrier pair is named per mode: a flight has an AWB and an
              airline, a truck has neither a vessel nor a shipping line. */}
          {isSea ? (
            <VoyageRow
              label={t('map.shipment.vessel', 'Vessel')}
              value={firstValue(shipment.current_vessel, shipment.vessel, shipment.booking_vessel)}
            />
          ) : (
            <VoyageRow
              label={mode === 'air' ? t('map.shipment.awb', 'AWB') : t('map.shipment.truckId', 'Truck ID')}
              value={firstValue(shipment.awb, shipment.truck_id)}
            />
          )}
          <VoyageRow
            label={isSea ? t('map.shipment.shippingLine', 'Shipping line') : t('map.shipment.airline', 'Airline')}
            value={isSea
              ? firstValue(shipment.shipping_companies, shipment.shipping_line, shipment.carrier)
              : firstValue(shipment.airlines)}
          />
          <VoyageRow
            label={t('map.shipment.departure', 'Departure')}
            value={firstValue(shipment.departure_port, shipment.pol, shipment.origin)}
          />
          <VoyageRow
            label={t('map.shipment.destination', 'Destination')}
            value={firstValue(shipment.arrival_port, shipment.destination_country)}
          />
          <VoyageRow
            label={t('map.shipment.submarket', 'Submarket')}
            value={firstValue(shipment.submarket)}
          />
          <VoyageRow
            label={t('map.shipment.containers', 'Containers')}
            value={containers}
          />
          <VoyageRow
            label={t('map.shipment.totalWeight', 'Total weight')}
            value={fmtWeight(shipment.total_weight)}
          />
          {/* Air and truck rows have no container split section, so the party
              and variety detail the old map popup carried lands here instead of
              being dropped on the way to the shared panel. */}
          {!isSea ? (
            <>
              <VoyageRow label={t('map.shipment.varieties', 'Varieties')} value={firstValue(shipment.varieties)} />
              <VoyageRow label={t('map.shipment.exporter', 'Exporter')} value={firstValue(shipment.exporters, shipment.exporter)} />
              <VoyageRow label={t('map.shipment.importer', 'Importer')} value={firstValue(shipment.importers, shipment.importer)} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetailsPanel;
